import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { RuteroConnectorStatus, useUpsertRuteroConnector } from '../queries';

interface Props {
  agencyId: number;
  connector: RuteroConnectorStatus | null;
  onClose: () => void;
}

/**
 * Form de credenciales de un conector de Rutero. WRITE-ONLY: los campos
 * arrancan SIEMPRE vacíos (las credenciales guardadas jamás vuelven del
 * backend) y guardar reemplaza el set completo de credenciales del conector.
 */
export function RuteroConnectorFormModal({ agencyId, connector, onClose }: Props) {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const upsert = useUpsertRuteroConnector(agencyId);
  const [values, setValues] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(true);
  const [integrationName, setIntegrationName] = useState('');
  const [environment, setEnvironment] = useState<'TEST' | 'PROD'>('TEST');
  const [webhookSecret, setWebhookSecret] = useState('');

  // Al abrir con otro conector, resetear el form (siempre vacío: write-only).
  useEffect(() => {
    setValues({});
    setIntegrationName(connector?.integration?.name ?? '');
    setEnvironment(connector?.integration?.environment ?? 'TEST');
    setWebhookSecret('');
    setEnabled(connector ? !connector.configured || connector.enabled : true);
  }, [connector]);

  if (!connector) return null;

  const invalidIntegration =
    connector.connector === 'transferz' &&
    ((Boolean(integrationName) &&
      (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(integrationName) ||
        integrationName.length < 3 ||
        integrationName.length > 64)) ||
      (Boolean(webhookSecret) && !integrationName) ||
      webhookSecret.length > 8192);
  const missingRequired =
    invalidIntegration || connector.fields.some((field) => field.required && !values[field.name]?.trim());

  const handleSubmit = async () => {
    try {
      await upsert.mutateAsync({
        connector: connector.connector,
        vertical: connector.vertical,
        credentials: Object.fromEntries(
          Object.entries(values)
            .map(([key, value]) => [key, value.trim()])
            .filter(([, value]) => value),
        ),
        enabled,
        ...(connector.connector === 'transferz' && integrationName
          ? {
              integration: { name: integrationName, environment, ...(webhookSecret ? { webhookSecret } : {}) },
            }
          : {}),
      });
      addToast({ message: t('rutero.connectors.saveSuccess'), color: 'success' });
      onClose();
    } catch (error) {
      addToast({
        message: t(
          axios.isAxiosError(error) && error.response?.status === 409
            ? 'rutero.connectors.integrationConflict'
            : 'rutero.connectors.saveError',
        ),
        color: 'error',
      });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {t('rutero.connectors.formTitle', { connector: connector.connector })}
        <Chip size="small" label={t(`rutero.verticals.${connector.vertical}`)} variant="outlined" />
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {connector.configured && <Alert severity="info">{t('rutero.connectors.writeOnlyNotice')}</Alert>}
          {connector.connector === 'transferz' && (
            <>
              <Alert severity="info">{t('rutero.connectors.integrationNotice')}</Alert>
              <TextField
                label={t('rutero.connectors.integrationName')}
                value={integrationName}
                onChange={(event) => setIntegrationName(event.target.value)}
                size="small"
                fullWidth
                disabled={Boolean(connector.integration)}
              />
              <TextField
                select
                label={t('rutero.connectors.integrationEnvironment')}
                value={environment}
                onChange={(event) => setEnvironment(event.target.value as 'TEST' | 'PROD')}
                disabled={Boolean(connector.integration)}
                size="small"
                fullWidth
              >
                <MenuItem value="TEST">TEST</MenuItem>
                <MenuItem value="PROD">PROD</MenuItem>
              </TextField>
              {connector.integration && (
                <>
                  <TextField
                    label={t('rutero.connectors.webhookPath')}
                    value={connector.integration.webhookPath}
                    InputProps={{ readOnly: true }}
                    size="small"
                    fullWidth
                  />
                  <Chip
                    label={t(
                      connector.integration.secretConfigured
                        ? 'rutero.connectors.secretConfigured'
                        : 'rutero.connectors.secretPending',
                    )}
                  />
                </>
              )}
              {!connector.integration?.secretConfigured && (
                <TextField
                  type="password"
                  autoComplete="new-password"
                  label={t('rutero.connectors.webhookSecret')}
                  value={webhookSecret}
                  onChange={(event) => setWebhookSecret(event.target.value)}
                  size="small"
                  fullWidth
                />
              )}
            </>
          )}
          {connector.fields.map((field) => (
            <TextField
              key={field.name}
              label={field.name}
              type={field.type === 'password' ? 'password' : 'text'}
              required={field.required}
              value={values[field.name] ?? ''}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  [field.name]: event.target.value,
                }))
              }
              autoComplete="new-password"
              fullWidth
              size="small"
            />
          ))}
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            }
            label={t('rutero.connectors.enabledLabel')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('rutero.connectors.cancel')}</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={upsert.isLoading}
          disabled={missingRequired}
        >
          {t('rutero.connectors.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
